<template>
  <div class="event-description-page">
    <spinner v-if="!event" />
    <main
      v-else
      class="new-design content single-event single-event-main-container"
      style="--featuredcolor: #0d0d0d"
    >
      <article class="single-event-main--cover-container">
        <img
          v-if="event.cover"
          class="single-event-cover-photo"
          style="max-height: 567.5px; max-width: 1009.33px"
          :alt="event.title"
          :src="event.cover"
        >
        <img
          v-else
          class="single-event-cover-photo"
          alt="Empty Event Cover"
          src="/assets/nocover.png"
          style="max-height: 567.5px; max-width: 1009.33px"
        >
      </article>
      <article class="single-event-main-content-container">
        <section class="single-event-cover">
          <div class="single-event-cover__genre-container">
            <span
              v-if="event.eventCategory && event.eventCategory.name"
              class="single-event-cover__genre-text-wrapper"
            >
              {{ event.eventCategory.name }}
            </span>
          </div>
          <div class="single-event-cover__description-container">
            <h1 class="single-event-cover-title">
              {{ event.title }}
            </h1>
            <div class="single-event-cover__info-container">
              <div class="single-event-cover__info-wrapper">
                Hosted by
                <a class="org-link" :href="`${landingURL()}/organizers/${organizationSlug}/`">
                  <div class="single-event__link-wrapper single-event__organizer-wrapper">
                    <div
                      v-if="event.organization.logo"
                      class="single-event--organizer-logo--wrapper"
                    >
                      <img
                        class="single-event--organizer-logo"
                        :src="event.organization.logo"
                        :alt="`${event.organization.publicName} Logo`"
                      >
                    </div>
                    <div class="single-event--organizer-name">
                      {{ event.organization.publicName }}
                    </div>
                  </div>
                </a>
                {{ event.metadata.facebookLink || event.metadata.webLink ? 'Web' : '' }}
                <div
                  v-if="event.metadata.facebookLink || event.metadata.webLink"
                  class="single-event__link-wrapper single-event__organizer-wrapper"
                >
                  <a
                    v-if="event.metadata.facebookLink"
                    class="web-link"
                    :href="event.metadata.facebookLink"
                  >
                    <div>
                      <div class="single-event--event-links--wrapper">
                        <img src="/assets/facebook.svg" alt="facebook icon">
                      </div>
                    </div>
                  </a>
                  <a v-if="event.metadata.webLink" class="web-link" :href="event.metadata.webLink">
                    <div>
                      <div class="single-event--event-links--wrapper">
                        <img src="/assets/web_link.svg" alt="web link icon">
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div class="single-event__buy-ticket-container">
            <div v-if="new Date(event.to) > new Date()" class="theater-event">
              <div
                :class="[
                  'single-event__single-ticket-container',
                  { 'canceled-event': event.canceled },
                ]"
              >
                <v-icon class="single-event__calendar-icon">event</v-icon>
                <div
                  v-if="moment(event.from).isSame(moment(event.to), 'day')"
                  class="single-event__ticket-info-container"
                >
                  <div>
                    <strong>{{ moment(event.from).format('DD.MM.YYYY. • ddd')
                    }}<span class="date-separator"> •</span></strong>
                  </div>
                  <div class="single-event__event-end-date">
                    {{ moment(event.from).format('HH:mm') }} hours
                  </div>
                </div>
                <div v-else class="single-event__ticket-info-container">
                  <div>
                    <strong>{{ moment(event.from).format('DD.MM.YYYY. HH:mm') }}</strong> hours<span
                      class="date-separator"
                    >
                      –</span>
                  </div>
                  <div class="single-event__event-end-date">
                    to
                    <span class="single-event__end-date">{{
                      moment(event.to).format('DD.MM.YYYY. HH:mm')
                    }}</span>
                    hours
                  </div>
                </div>

                <span v-if="event.canceled" class="unavailable canceled">Canceled</span>
                <span v-else-if="event.soldOut" class="unavailable">Sold Out</span>
                <a
                  class="button-primary-large single-event__buy-ticket-button"
                  target="_blank"
                  :href="`${frontendURL()}/events/${event.slug}/register`"
                >
                  Buy Tickets
                </a>
              </div>
              <div
                v-if="event.canceled && event.cancellationNotification"
                class="cancel-notification"
                v-html="event.cancellationNotification"
              />
            </div>
          </div>
        </section>

        <section
          v-if="
            event.highlightedData &&
              ((event.highlightedData.ageRestriction && event.highlightedData.ageRestriction.show) ||
              (event.highlightedData.tableReservation &&
              event.highlightedData.tableReservation.show) ||
              (event.highlightedData.freeEntranceWithAgeLimit &&
              event.highlightedData.freeEntranceWithAgeLimit.show))
          "
          class="single-event__highlighted-data"
        >
          <div class="single-event__content-header">Useful information</div>
          <div class="single-event__highlighted-data-container">
            <div
              v-if="
                event.highlightedData.ageRestriction && event.highlightedData.ageRestriction.show
              "
              class="single-event__highlighted-data-container-item"
            >
              <p class="single-event__highlighted-data-container-item-title">Age limit:</p>
              <div v-if="event.highlightedData.ageRestriction.enabled">
                {{ event.highlightedData.ageRestriction.value }}
              </div>
              <div v-else>No</div>
            </div>

            <div
              v-if="
                event.highlightedData.freeEntranceWithAgeLimit &&
                  event.highlightedData.freeEntranceWithAgeLimit.show
              "
              class="single-event__highlighted-data-container-item"
            >
              <p class="single-event__highlighted-data-container-item-title">
                Free admission for children up to age:
              </p>
              <div v-if="event.highlightedData.freeEntranceWithAgeLimit.enabled">
                {{ event.highlightedData.freeEntranceWithAgeLimit.value }}
              </div>
              <div v-else>No</div>
            </div>

            <div
              v-if="
                event.highlightedData.tableReservation &&
                  event.highlightedData.tableReservation.show
              "
              class="single-event__highlighted-data-container-item"
            >
              <p class="single-event__highlighted-data-container-item-title">Table reservation:</p>
              <div v-if="event.highlightedData.tableReservation.enabled">Yes</div>
              <div v-else>No</div>
            </div>
          </div>
        </section>

        <section class="single-event-content-container">
          <div class="single-event__description-container">
            <div class="single-event__content-header">Description</div>
            <div class="single-event__content" v-html="event.description" />

            <section class="single-event__faqs">
              <div v-if="event.faqs && event.faqs.length" class="single-event__content-header">
                Frequently asked questions
              </div>
              <div class="single-event__faqs-container">
                <div class="single-event__faqs-list">
                  <div
                    v-for="(faq, index) in event.faqs"
                    :key="index"
                    class="single-event__faqs-question"
                    role="button"
                    tabindex="0"
                    aria-expanded="false"
                    :aria-controls="`faq-answer-${index}`"
                    onkeydown="if($event.key === 'Enter' || $event.key === ' ') toggleFaq($event)"
                    @click="toggleFaq($event)"
                  >
                    <div class="faq-header">
                      <span class="faqs-question-text">{{ faq.question }}</span>
                      <span class="faqs-icon" aria-hidden="true">+</span>
                    </div>
                    <div :id="`faq-answer-${index}`" class="faqs__answer" aria-hidden="true">
                      <p>{{ faq.answer }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
          <div
            v-if="
              event.location &&
                event.location.latitude &&
                event.location.longitude &&
                event.location.venue &&
                event.location.city &&
                event.location.address1
            "
            class="single-event__location-container"
          >
            <div class="single-event__location-header">Venue</div>
            <div class="single-event__map-container">
              <gmap-map
                id="map"
                :style="`height: ${
                  !event.location.latitude && !event.location.longitude ? '0' : '426px'
                }`"
                :zoom="13"
                :center="{
                  lat: event.location.latitude,
                  lng: event.location.longitude,
                }"
                :options="{
                  disableDefaultUI: true,
                  fullscreenControl: true,
                }"
                class="map"
              >
                <gmap-marker
                  :position="{
                    lat: event.location.latitude,
                    lng: event.location.longitude,
                  }"
                  label="■"
                />
              </gmap-map>
              <a
                :href="`https://www.google.com/maps/search/?api=1&query=${event.location.venue}+${event.location.address1}+${event.location.city}+${event.location.country}`"
                target="_blank"
                aria-label="link to google maps"
              >
                <div class="single-event__map-location-info">
                  <div>
                    <div>
                      <strong>
                        {{ event.location.venue }}
                      </strong>
                    </div>
                    <div class="single-event__map-location-info--details">
                      <span v-if="event.location.venue">{{ event.location.venue }}</span>
                      <span v-if="event.location.city">{{ event.location.city }}</span>
                      <span v-if="event.location.address1">{{ event.location.address1 }}</span>
                    </div>
                  </div>

                  <button id="dp-next">
                    <v-icon size="56" color="black" class="map-chevron">chevron_right</v-icon>
                  </button>
                </div>
              </a>
            </div>
            <div class="link-container" style="display: none">
              <div class="social-links-container">
                <a target="_blank" href="https://www.facebook.com/share.php">
                  <img src="/assets/ic_facebook.svg" alt="facebook">
                </a>
                <a target="_blank" href="https://twitter.com/intent/tweet">
                  <img src="/assets/ic_twitter.svg" alt="twitter">
                </a>
                <a target="_blank" href="https://www.linkedin.com/shareArticle">
                  <img src="/assets/ic_linkedin.svg" alt="linkedin">
                </a>
                <a :href="`mailto:contact@core-event.co?subject=${event.title}`">
                  <img src="/assets/ic_email.svg" alt="email">
                </a>
                <button class="link-button">
                  <img src="/assets/ic_link.svg" alt="link">
                </button>
              </div>
            </div>
          </div>
          <div class="single-event__organizer">
            <div class="single-event__organizer--header">Hosted by</div>
            <div class="single-event__organizer--container">
              <a :href="`${landingURL()}/organizers/${organizationSlug}`" class="logo-link">
                <div v-if="event.organization.logo" class="single-event__organizer--logo-container">
                  <img
                    :src="event.organization.logo"
                    :alt="`${event.organization.publicName} Logo`"
                  >
                </div>
                <div class="single-event__organizer--info-container">
                  <div class="org-info">
                    <div class="single-event__organizer--name">
                      {{ event.organization.publicName }}
                    </div>
                    <div class="single-event__organizer--description">
                      {{ event.organization.description }}
                    </div>
                  </div>

                  <div class="single-event__organizer--social-container">
                    <a
                      v-if="event.organization.webLink"
                      :href="`${
                        event.organization.webLink.startsWith('https://') ? '' : 'https://'
                      }${event.organization.webLink}`"
                      target="_blank"
                    >
                      <img src="/assets/ic_link.svg" alt="organizer link icon">
                    </a>
                    <a :href="`mailto:${event.organization.email}`" target="_blank">
                      <img src="/assets/ic_email.svg" alt="organier email icon">
                    </a>
                    <a
                      v-if="event.organization.facebookLink"
                      :href="event.organization.facebookLink"
                      target="_blank"
                    >
                      <img src="/assets/ic_facebook.svg" alt="facebook icon">
                    </a>
                    <a
                      v-if="event.organization.instagramLink"
                      :href="event.organization.instagramLink"
                      target="_blank"
                    >
                      <img src="/assets/ic_instagram.svg" alt="instagram icon">
                    </a>
                  </div>
                </div>
                <div class="single-event__chevron" />
              </a>
            </div>
          </div>
        </section>
      </article>
      <div class="social-sidebar-wrapper">
        <div class="social-links-container">
          <a target="_blank" href="https://www.facebook.com/share.php">
            <img src="/assets/ic_facebook.svg" alt="facebook">
          </a>
          <a target="_blank" href="https://twitter.com/intent/tweet">
            <img src="/assets/ic_twitter.svg" alt="twitter">
          </a>
          <a target="_blank" href="https://www.linkedin.com/shareArticle">
            <img src="/assets/ic_linkedin.svg" alt="linkedin">
          </a>
          <a :href="`mailto:contact@core-event.co?subject=${event.title}`">
            <img src="/assets/ic_email.svg" alt="email">
          </a>
          <button class="link-button">
            <img src="/assets/ic_link.svg" alt="link">
          </button>
        </div>
      </div>
    </main>
  </div>
</template>
<script>
import { GET_EVENT_PREVIEW_DATA } from '@/graphql/event.graphql'
import { frontendURL, landingURL } from '@/utils'
import { round } from 'lodash'
import moment from 'moment'

export default {
  name: 'event-preview',
  components: {},
  props: {
    organizationSlug: String,
    eventId: String,
  },
  data() {
    return {
      event: null,
      openFaqs: [],
    }
  },
  computed: {
    moment() {
      return moment
    },
  },
  async mounted() {
    await this.fetchEventData()
  },
  methods: {
    frontendURL() {
      return frontendURL
    },
    round,
    landingURL() {
      return landingURL
    },
    async fetchEventData() {
      const {
        data: { getEventPreviewData },
      } = await this.$apollo.query({
        query: GET_EVENT_PREVIEW_DATA,
        variables: {
          eventId: this.eventId,
        },
      })

      this.event = getEventPreviewData
    },
    minimumOrderItemPrice() {
      if (!this.event || !this.event.orderItems) {
        return 0
      }

      return Math.min(this.event.orderItems.map(orderItem => orderItem.price))
    },
    toggleFaq(event) {
      const el = event.currentTarget
      const expanded = el.getAttribute('aria-expanded') === 'true'
      const icon = el.querySelector('.faqs-icon')
      const controls = el.getAttribute('aria-controls')
      const answer = document.getElementById(controls)

      const newExpanded = !expanded

      el.setAttribute('aria-expanded', newExpanded)

      answer.classList.toggle('faqs__answer--open', newExpanded)
      answer.setAttribute('aria-hidden', !newExpanded)

      icon.textContent = newExpanded ? '-' : '+'
    },
  },
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

[data-responsive-background-image] {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

[data-responsive-background-image] > img {
  display: none;
}

html {
  scroll-behavior: smooth;
  height: 100%;

  @media screen and (max-width: 576px) {
    font-size: 75%;
  }
}

.event-description-page {
  font-size: calc(10px + 1vmin);
  font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100%;
  display: grid;
  grid-template-rows: auto 1fr auto;
}

a {
  color: inherit;
  text-decoration: none;
}

a:not([href]) {
  cursor: default;
}

h1 {
  font-family: 'Raleway', sans-serif;
  font-weight: 800;
  font-size: 3rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  color: #0277bd;
  margin-bottom: 1.5rem;
}

h2 {
  font-family: 'Raleway', sans-serif;
  font-weight: 800;
  font-size: 3rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  color: #004c8c;
  margin-bottom: 1.5rem;
}

h3 {
  font-family: 'Raleway', sans-serif;
  font-weight: 800;
  font-size: 2.25rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  color: #0277bd;
  margin-bottom: 1rem;
}

h4 {
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  font-size: 1.5rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  color: #333333;
  margin-bottom: 1.5rem;
}

::v-deep p {
  font-size: 1rem;
  line-height: 150%;
  color: #333;
}

@media screen and (max-width: 576px) {
  p,
  ::v-deep .single-event__content p {
    font-size: 1.5rem !important;
  }

  p + p {
    margin-top: 1.5rem !important;
  }
}

p.large + p {
  margin-top: 1.25rem;
}

p.medium + p {
  margin-top: 0.5rem;
}

h5 {
  font-family: Raleway, sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 1.25rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  color: #fff;
}

.new-design .social-sidebar-wrapper {
  position: absolute;
  grid-row: 0 / span 0;
  grid-column: 0 / span 0;
}

.new-design.header-menu > ul > li,
.new-design .header-menu > ul > li {
  margin: 0 0.5rem;
}

.new-design.header__dropdown-content > a,
.new-design .header__dropdown-content > a {
  padding: 0.5313rem 1rem;
  transition: all 0.15s ease-in-out;
}

.new-design.header__dropdown-content > a:hover,
.new-design .header__dropdown-content > a:hover {
  color: #0277bd;
  background-color: #e6f1f8;
}

.new-design.header__dropdown-content > a:active,
.new-design .header__dropdown-content > a:active {
  background-color: #cee4f2;
  color: #004c8c;
}

.new-design .footer *,
.new-design.footer * {
  color: #333;
}

.new-design .footer h4,
.new-design.footer h4 {
  margin-bottom: 1.25rem;
}

.new-design .footer ul li,
.new-design.footer ul li {
  display: flex;
}

.new-design .footer ul li + li,
.new-design.footer ul li + li {
  margin-top: 0.75rem;
}

.new-design .footer-bottom__legal-new > a,
.new-design.footer-bottom__legal-new > a {
  display: flex;
}

.new-design .footer-bottom__logo > a,
.new-design.footer-bottom__logo > a {
  display: flex;
}

.new-design div.social-links-container {
  position: sticky;
  display: flex;
  flex-direction: column;
  background: #e6f1f8;
  border-radius: 24px;
  grid-row: 0 / span 0;
  grid-column: 0 / span 0;
  overflow: hidden;
}

.new-design div.social-links-container > a:hover,
.new-design div.social-links-container > button:hover {
  overflow: hidden;
  backdrop-filter: brightness(0) saturate(100%) invert(91%) sepia(7%) saturate(673%)
    hue-rotate(163deg) brightness(100%) contrast(91%);
}

.new-design div.social-links-container > a:active,
.new-design div.social-links-container > button:active {
  overflow: hidden;
  backdrop-filter: brightness(0) saturate(100%) invert(19%) sepia(43%) saturate(3618%)
    hue-rotate(191deg) brightness(96%) contrast(101%);
}

.new-design div.social-links-container > a:active > div,
.new-design div.social-links-container > button:active > div {
  filter: brightness(0) saturate(100%) invert(84%) sepia(27%) saturate(146%) hue-rotate(160deg)
    brightness(99%) contrast(99%);
}

.new-design div.social-links-container img {
  padding: 0.75rem;
}

@media (max-width: 576px) {
  .new-design div.social-links-container {
    z-index: 1;
    grid-row: 0/0;
    grid-column: 0/0;
    justify-self: end;
    align-self: flex-end;
    transform: translate(0px, -123px) translate(0, 50%);
  }
}

.new-design.content {
  display: grid;
  grid-template-columns:
    [full-start] minmax(1em, 1fr) [main-start] minmax(0, Min(calc(100% - 6rem), 1216px))
    [main-end] minmax(1em, 1fr) [full-end];
  margin: 2.5rem 0 15rem;
  gap: 0;
}

@media (max-width: 992px) {
  .new-design.content {
    margin-top: 5rem;
  }
}

@media (max-width: 576px) {
  .new-design.content {
    margin-top: 4rem;
  }
}

.new-design.content > * {
  grid-column: main;
}

.new-design.content #map {
  grid-column: full;
}

.new-design.single-event,
.new-design .single-event {
  margin-top: 90px;
}

.new-design.single-event ol,
.new-design .single-event ol,
.new-design.single-event ul,
.new-design .single-event ul {
  list-style-position: inside;
}

.new-design.single-event div.social-links-container,
.new-design .single-event div.social-links-container {
  background-color: #f5f5f5;
}

@media (max-width: 768px) {
  .new-design.single-event div.social-links-container,
  .new-design .single-event div.social-links-container {
    display: none;
  }
}

.new-design.single-event-main-container,
.new-design .single-event-main-container {
  position: relative;
}

.new-design.single-event-main-container > article,
.new-design .single-event-main-container > article {
  grid-column: main;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  column-gap: 1.5rem;
}

.new-design.single-event-main-container > article > *,
.new-design .single-event-main-container > article > * {
  grid-column: 2/-2;
}

@media (max-width: 576px) {
  .new-design.single-event-main-container > article,
  .new-design .single-event-main-container > article {
    grid-template-columns: repeat(6, 1fr);
    column-gap: 1rem;
  }

  .new-design.single-event-main-container > article > *,
  .new-design .single-event-main-container > article > * {
    grid-column: 1/-1;
  }
}

.new-design.single-event-main-container .single-event-main-content-container,
.new-design .single-event-main-container .single-event-main-content-container {
  margin: 0 -1rem;
}

.new-design.single-event-main--cover-container,
.new-design .single-event-main--cover-container {
  margin-bottom: 3.5rem;
}

@media (max-width: 576px) {
  .new-design.single-event-main--cover-container,
  .new-design .single-event-main--cover-container {
    grid-column: full !important;
  }
}

.new-design.single-event-cover__genre-container,
.new-design .single-event-cover__genre-container {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 700;
  font-size: 1rem;
  line-height: 120%;
  letter-spacing: 0.015em;
  color: #333;
  margin-bottom: 1.5rem;
}

.new-design.single-event-cover__genre-text-wrapper,
.new-design .single-event-cover__genre-text-wrapper {
  padding: 0 0 0.25rem;
  border-bottom: 0.5px solid #333;
}

.new-design.single-event-cover__description-container,
.new-design .single-event-cover__description-container {
  margin-bottom: 3.5rem;
}

.new-design.single-event-cover-title,
.new-design .single-event-cover-title {
  color: #333;
}

@media (max-width: 576px) {
  .new-design.single-event-cover-title,
  .new-design .single-event-cover-title {
    margin-bottom: 2rem;
  }
}

.new-design.single-event-cover__info-container,
.new-design .single-event-cover__info-container {
  color: #666;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 2rem;
  margin-bottom: 3.5rem;

  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1.25rem;
  line-height: 150%;
}

.new-design.single-event-cover__info-wrapper,
.new-design .single-event-cover__info-wrapper {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0 0.75rem;
}

.new-design.single-event__link-wrapper,
.new-design .single-event__link-wrapper {
  display: flex;
  flex-wrap: nowrap;
  padding: 0.75rem 1rem;
  background-color: #f5f5f5;
  border-radius: 64px;
  gap: 0 1.125rem;
  flex: 1 1 0;
  max-height: 3rem;
  justify-content: center;
  align-items: center;
}

.new-design.single-event__organizer-wrapper,
.new-design .single-event__organizer-wrapper {
  padding: 0.5rem 1rem 0.5rem 0.5rem;
  gap: 0.5rem;
}

.new-design.single-event--organizer-logo,
.new-design .single-event--organizer-logo {
  width: 100%;
  height: 100%;
}

.new-design.single-event--organizer-logo--wrapper,
.new-design .single-event--organizer-logo--wrapper {
  display: flex;
  align-items: center;
  border-radius: 32px;
  border: 1px solid rgba(151, 151, 151, 0.5);
  background-color: #fff;
  width: 2rem;
  height: 2rem;
  overflow: hidden;
}

.new-design.single-event--event-links--wrapper,
.new-design .single-event--event-links--wrapper {
  display: flex;
  align-items: center;
  border-radius: 32px;
  width: 2rem;
  height: 2rem;
  overflow: hidden;
}

.new-design.single-event--organizer-name,
.new-design .single-event--organizer-name {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 150%;
  text-align: center;
  color: #333;
}

.new-design.single-event-cover-photo,
.new-design .single-event-cover-photo {
  width: 100%;
  display: block;
  object-fit: cover;
  height: auto;
  border-radius: 4px;
}

@media (max-width: 576px) {
  .new-design.single-event-cover-photo,
  .new-design .single-event-cover-photo {
    grid-column: -1;
    border-radius: 0;
  }
}

.new-design.single-event__buy-ticket-container,
.new-design .single-event__buy-ticket-container {
  display: flex;
  flex-direction: column;
}

.new-design.single-event__single-ticket-container,
.new-design .single-event__single-ticket-container {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1.5rem;
  line-height: 1.5;
  color: #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1rem 1rem 1.25rem;
  border: 1px solid rgba(151, 151, 151, 0.5);
  border-radius: 48px;
  gap: 1rem 1rem;

  @media screen and (max-width: 1022px) {
    flex-direction: column;
  }
}

.single-event__single-ticket-container:hover {
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.04), 0 4px 8px rgba(0, 0, 0, 0.04);
}

.single-event__single-ticket-container:has(.single-event__buy-ticket-button:active) {
  box-shadow: 0 4px 40px rgba(51, 51, 51, 0.32);
}

.single-event__calendar-icon {
  width: 24px;
  height: 24px;

  @media screen and (max-width: 1022px) {
    display: none;
  }
}

.new-design.single-event__ticket-price,
.new-design .single-event__ticket-price {
  font-size: 1rem;
  display: none;
}

@media (max-width: 576px) {
  .new-design.single-event__ticket-price,
  .new-design .single-event__ticket-price {
    display: block;
  }
}

.new-design.single-event__ticket-info-container,
.new-design .single-event__ticket-info-container {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  color: #333333;
  font-size: 24px;
  font-family: Source Sans Pro;
  font-weight: 400;
  word-wrap: break-word;
  @media screen and (max-width: 1022px) {
    flex-direction: column;
    .date-separator {
      display: none;
    }
  }
}

.single-event__event-end-date {
  @media screen and (max-width: 1022px) {
    color: #666;
    font-family: Source Sans Pro;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
}

.single-event__end-date {
  color: #666;
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  @media screen and (max-width: 1022px) {
    font-size: 14px;
  }
}

.new-design.single-event__buy-ticket-button,
.new-design .single-event__buy-ticket-button {
  font-size: 1.125rem;
}

.new-design.single-event__highlighted-data-container,
.new-design .single-event__highlighted-data-container {
  margin-top: 1rem;
  margin-bottom: 3.5rem;
  display: flex;
  flex-direction: row;
  gap: 24px;
}

@media (max-width: 576px) {
  .new-design.single-event__highlighted-data-container,
  .new-design .single-event__highlighted-data-container {
    margin-bottom: 4rem;
    flex-direction: column;
    gap: 16px;
  }
}

.new-design.single-event__highlighted-data-container-item,
.new-design .single-event__highlighted-data-container-item {
  flex-basis: 33%;
  padding: 16px;
  border: 1px solid #979797;
  border-radius: 4px;
  color: #0277bd;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 150%;
}

@media (max-width: 576px) {
  .new-design.single-event__highlighted-data-container-item,
  .new-design .single-event__highlighted-data-container-item {
    color: #333;
    font-weight: 400;
    line-height: 100%;
  }
}

.new-design.single-event__highlighted-data-container-item-title,
.new-design .single-event__highlighted-data-container-item-title {
  display: block;
  color: #333;
  font-size: 1rem;
  font-weight: 400;
  line-height: 150%;
  margin-bottom: 8px;
}

@media (max-width: 576px) {
  .new-design.single-event__highlighted-data-container-item-title,
  .new-design .single-event__highlighted-data-container-item-title {
    margin-bottom: 4px;
  }
}

.new-design.single-event__faqs-container,
.new-design .single-event__faqs-container {
  margin-top: 1rem;
}

.new-design.single-event__faqs-list,
.new-design .single-event__faqs-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: #333;
}

.new-design.single-event__faqs-question,
.new-design .single-event__faqs-question {
  position: relative;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 1.5rem;
  transition: background-color 0.3s ease;
  cursor: pointer;
}

@media (max-width: 576px) {
  .new-design.single-event__faqs-question,
  .new-design .single-event__faqs-question {
    padding: 16px;
  }
}

.new-design.single-event__faqs-question .faq-header,
.new-design .single-event__faqs-question .faq-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #666;
}

.new-design.single-event__faqs-question .faqs-question-text,
.new-design .single-event__faqs-question .faqs-question-text {
  display: inline-block;
  font-size: 1.2rem;
  font-weight: bold;
  margin-right: 2rem;
}

.new-design.single-event__faqs-question .faqs-icon,
.new-design .single-event__faqs-question .faqs-icon {
  font-size: 1.8rem;
  pointer-events: none;
}

.new-design.single-event__faqs-question .faqs__answer,
.new-design .single-event__faqs-question .faqs__answer {
  margin-top: 0.75rem;
  overflow: hidden;
  max-height: 0;
  will-change: max-height;
  transition: max-height 0.5s ease-in-out;
}

.new-design .single-event__faqs-question .faqs__answer--open {
  max-height: 500px !important;
}

.new-design.single-event__faqs-question .faqs__answer p,
.new-design .single-event__faqs-question .faqs__answer p {
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
}

.new-design.single-event__description-container,
.new-design .single-event__description-container {
  word-break: break-word;
  margin-bottom: 3.5rem;
}

.new-design.single-event__content,
.new-design .single-event__content {
  margin-bottom: 3.5rem;
}

.new-design.single-event__content-header,
.new-design .single-event__content-header {
  margin-bottom: 0.5rem;
}

.new-design.single-event__music-links a,
.new-design .single-event__music-links a {
  display: flex;
  align-items: center;
}

.new-design.single-event__music-links .single-event__link-wrapper,
.new-design .single-event__music-links .single-event__link-wrapper {
  flex: 0;
}

.new-design.single-event__location-container,
.new-design .single-event__location-container {
  margin-bottom: 3.5rem;
}

@media screen and (max-width: 1218px) {
  .new-design.single-event__location-container,
  .new-design .single-event__location-container {
    margin-bottom: 0;
  }
}

.new-design.single-event__map-container,
.new-design .single-event__map-container {
  border: 1px solid rgba(151, 151, 151, 0.5);
  border-radius: 4px;
}

.new-design.single-event__location-header,
.new-design .single-event__location-header {
  color: #666;
  margin-bottom: 0.5rem;
}

.new-design.single-event__map-location-info,
.new-design .single-event__map-location-info {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1.5rem;
  line-height: 1.5;
  color: #333;
  margin-right: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
}

.new-design.single-event__map-location-info--details,
.new-design .single-event__map-location-info--details {
  font-size: 1rem;
  color: #666;
}

.new-design.single-event__chevron,
.new-design .single-event__chevron {
  display: none;
  height: 3.5rem;
  width: 3.5rem;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  cursor: pointer;
}

.new-design.single-event__organizer--header,
.new-design .single-event__organizer--header {
  color: #666;
  margin-bottom: 0.5rem;
}

.new-design.single-event__organizer--container,
.new-design .single-event__organizer--container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(151, 151, 151, 0.5);
  border-radius: 4px;
  padding: 2rem 2rem 2.5rem 2.5rem;
}

.new-design.single-event__organizer--logo-container,
.new-design .single-event__organizer--logo-container {
  border: 1px solid rgba(151, 151, 151, 0.5);
  display: flex;
  align-items: center;
  overflow: hidden;
  border-radius: 100%;
}

.new-design.single-event__organizer--logo-container img,
.new-design .single-event__organizer--logo-container img {
  width: 4rem;
  height: 4rem;
}

.new-design.single-event__organizer--info-container,
.new-design .single-event__organizer--info-container {
  flex: 1;
  margin-left: 2.5rem;
  margin-right: 2rem;
}

.new-design.single-event__organizer--name,
.new-design .single-event__organizer--name {
  font-family: 'Raleway';
  font-style: normal;
  font-size: 2.25rem;
  font-weight: 800;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #333;
  margin-bottom: 1rem;
}

.new-design.single-event__organizer--description,
.new-design .single-event__organizer--description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1.25rem;
  line-height: 150%;
  color: #333;
  margin-bottom: 1rem;
}

.new-design.single-event__organizer--social-container,
.new-design .single-event__organizer--social-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  flex: 1 1 0;
}

.new-design.single-event #map,
.new-design .single-event #map {
  height: 400px;
}

.new-design #map {
  height: 450px;
}

.new-design .sales-location__input-container input {
  background: #fff;
  border: 2px solid #0277bd;
  border-radius: 8px 0 0 8px;
  height: 3.5rem;
  width: 21.5625rem;
  display: block;
  padding: 1.0625rem 0.375rem 1.0625rem 1rem;
}

.new-design .sales-location__input-container input:focus {
  outline: none !important;
  border: 2px solid #0277bd;
  border-radius: 8px 0 0 8px;
}

.new-design .sales-location__input-container button {
  background-color: #0277bd;
  padding: 1rem 0.625rem;
  border: none;
  border-radius: 0 8px 8px 0;
  cursor: pointer;
}

.new-design .sales-location__input-container button img {
  display: block;
}

.new-design.organizer-list__logo-container > img,
.new-design .organizer-list__logo-container > img {
  height: 100%;
  width: 100%;
}

.link-button {
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
}

.event-description-page:has(div#map) {
  overscroll-behavior-y: contain;
}

.single-event__content-header {
  color: #666666;
}

::v-deep .single-event__content p img {
  display: block;
  max-width: 60%;
  margin: 0 auto;
}

::v-deep .single-event__content iframe {
  display: block;
  width: 60%;
  aspect-ratio: 16 / 9;
  margin: 1rem auto;
}

@media screen and (max-width: 576px) {
  .single-event--organizer-name {
    word-wrap: break-word;
  }

  .single-event__single-ticket-container {
    display: flex !important;
    flex-direction: column;

    .single-event__ticket-info-container {
      display: flex !important;
      margin-bottom: 5%;
    }

    .single-event__buy-ticket-button {
      width: 100%;
      margin-top: 5%;
    }

    .single-event__ticket-price {
      display: none !important;
    }
  }

  .single-event__location-container {
    display: flex;
    flex-direction: column;
    gap: 8.125rem;
  }

  .link-container {
    display: block !important;

    .social-links-container {
      display: flex !important;
      flex-direction: row !important;
      justify-content: space-between;
      padding-right: 10%;
      flex-wrap: wrap;
      margin-top: 4rem;
    }
  }

  ::v-deep .single-event__content p img {
    max-width: 100%;
  }

  ::v-deep .single-event__content iframe {
    width: 100%;
  }
}

.single-section > h2,
h1,
h3 {
  color: #333333;
}

.organizer-section section {
  margin-bottom: 8rem;
}

.org-link {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
}

.web-link {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  padding-left: 0.5rem;
}

.nav-icon-close img {
  margin-top: 0.24rem;
}

.theater-event {
  margin-bottom: 1.5rem;
}

.logo-link {
  display: flex;
  align-items: center;

  @media screen and (max-width: 692px) {
    flex-direction: column;
    align-items: flex-start;
    .single-event__organizer--logo-container {
      margin-left: 2rem;
      margin-bottom: 2rem;
    }
  }
}

.single-event__organizer--container {
  @media screen and (max-width: 692px) {
    flex-direction: column;
    align-items: flex-start !important;
  }
}

.canceled {
  color: #bd2a3c;
}

.unavailable {
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings: 'clig' off, 'liga' off;
  font-family: 'Source Sans Pro';
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.cancel-notification {
  display: flex;
  padding: 16px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 4px;
  border: 1px solid #fbebed;
  border-top-right-radius: 0;
  border-top-left-radius: 0;

  p {
    color: #db384c !important;
    font-family: 'Source Sans Pro';
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
  }
}

.org-link {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
}

.nav-icon-close img {
  margin-top: 0.21rem;
}

h4 {
  margin: 10px 0 5px;
  font-size: 1.2rem;
  font-weight: bold;
}

p {
  font-size: 1rem;
  line-height: 1.5;
}

@import url('https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,500;0,600;0,700;0,800;1,500;1,600;1,700;1,800&family=Source+Sans+Pro:ital,wght@0,400;0,700;1,400;1,700&display=swap');

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
    margin: 0;
  }
  to {
    opacity: 1;
    transform: translateY(0);
    margin-top: -40px;
    margin-bottom: 100px;
  }
}

@keyframes invisible {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    transform: translateY(0);
    margin-bottom: 100px;
  }
  to {
    opacity: 0;
    transform: translateY(-50px);

    margin: 0;
  }
}

.button-primary-large {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 120%;
  letter-spacing: 0.015em;
  text-transform: uppercase;

  white-space: nowrap;
  color: #ffffff;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 1.5rem;
  gap: 0.625rem;
  background: #333333;
  border-radius: 24px;
  flex-wrap: nowrap;
  flex: 0 1 auto;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background-color: #0277bd;
  }

  &:active {
    background-color: #004c8c;
  }

  @media screen and (max-width: 992px) {
    width: 65%;
  }
}

h2 {
  color: #0277bd;
  margin: 9rem 3.125rem 3rem 0;
}

.dp-slider div {
  transform-style: preserve-3d;
}

.chevron-right {
  background-image: url('/assets/chevron-right.svg');
  background-repeat: no-repeat;
  background-position: center;
  height: 3.5rem;
  border-radius: 28px;
}

#dp-next {
  @media (max-width: 992px) {
    background: none;
    order: 2;
    border: none;
    cursor: pointer;
    right: 45%;
    transform: translateX(100%);
    bottom: 0;
  }
}

.dp-img img {
  display: block;
  text-align: center;
  width: 100%;
  border-radius: 0.75rem;
  opacity: 0;
  transition: opacity 0s linear 0.15s;
  -webkit-transition: opacity 0.15s ease-in 0.2s;
  -moz-transition: opacity 0.15s ease-in 0.2s;
  -ms-transition: opacity 0.15s ease-in 0.2s;
  -o-transition: opacity 0.15s ease-in 0.2s;
  @media (max-width: 536px) {
    border-radius: 1.5rem;
  }
  @media screen and (max-width: 992px) {
    border-radius: 0;
    height: unset;
  }
}

.button-container > * {
  margin-right: 0.625rem;
}

.ql-size-small {
  font-size: 0.5rem;
}

.ql-size-large {
  font-size: 1.5rem;
}

.ql-size-huge {
  font-size: 2.5rem;
}

::v-deep .single-event__content a {
  text-decoration: underline !important;
  cursor: pointer !important;
}

::v-deep .single-event__content ul {
  font-size: 1rem;
  font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
}

::v-deep .single-event__content ul:has(span.ql-size-small) {
  font-size: 0.5rem;
}

::v-deep .single-event__content ul:has(span.ql-size-large) {
  font-size: 1.5rem;
}

::v-deep .single-event__content ul:has(span.ql-size-huge) {
  font-size: 2.5rem;
}

::v-deep .vue-map div {
  filter: grayscale(100%);
}

::v-deep .map-chevron:hover {
  filter: invert(24%) sepia(100%) saturate(3938%) hue-rotate(188deg) brightness(98%) contrast(98%);
}
</style>
